import { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from 'src/shared/lib/useQuery';
import { useCallback } from 'react';
import { BASE_PATH } from 'src/features/flows/queries/useFlow';
import { useOrg } from 'src/shared/contexts/OrgContext';
import { genQueryKey } from 'src/shared/lib/query.utils';
import { AppConfig } from 'src/features/apps/types/app.types';

export function useFlowApps(): UseQueryResult<AppConfig[] | undefined>;
export function useFlowApps(appId: string): UseQueryResult<AppConfig | undefined>;
export function useFlowApps(appId: string, version: number): UseQueryResult<AppConfig | undefined>;
export function useFlowApps(appId: string, version?: number): UseQueryResult<AppConfig | undefined>;
export function useFlowApps(appId?: string, version?: number): UseQueryResult<AppConfig[] | AppConfig | undefined> {
  const { orgFetch, currentOrg } = useOrg();

  const queryKey = ['flow-apps', genQueryKey(currentOrg.id)];

  const select = useCallback(
    (data: AppConfig[]) => {
      if (appId && version) {
        const app = data.find((app) => app.id === appId);
        const versionConfig = app?.AppVersionHistory?.find((v) => v.version === version);
        return app
          ? {
              ...app,
              ...(versionConfig && {
                semver: versionConfig?.semver,
                variables: versionConfig?.variables,
                secrets: versionConfig?.secrets,
                linkedModels: versionConfig?.models,
              }),
            }
          : undefined;
      }
      if (appId) {
        return data.find((app) => app.id === appId);
      }
      return data;
    },
    [appId, version]
  );

  return useQuery({
    queryKey,
    queryFn: getData,
    select,
  });

  async function getData() {
    const res = await orgFetch<AppConfig[]>(`${BASE_PATH}/apps`);
    if (!res.ok) {
      throw new Error('Failed to fetch apps');
    }

    const data = await res.json();

    return data.map((app) => {
      const currentVersion = app.AppVersionHistory.find((v) => v.version === app.version);
      return {
        ...app,
        ...(currentVersion && {
          semver: currentVersion?.semver,
          variables: currentVersion?.variables,
          secrets: currentVersion?.secrets,
          linkedModels: currentVersion?.models,
        }),
      };
    });
  }
}
